import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { isSupportedSubmissionFile, MAX_SUBMISSION_SIZE, formatFileSize } from '@/lib/fileDownload';
import type { StudentSubmission } from '@/hooks/useStudentData';

interface SubmitModalProps {
  assignment: { id: string; title: string } | null;
  existing: StudentSubmission | null;
  userId: string;
  onClose: () => void;
  onSubmitted: () => void;
}

export default function SubmitModal({
  assignment,
  existing,
  userId,
  onClose,
  onSubmitted,
}: SubmitModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [note, setNote] = useState(existing?.note ?? '');
  const [link, setLink] = useState(existing?.link ?? '');
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  if (!assignment) return null;

  function handleFile(file: File | null) {
    if (!file) return;
    if (!isSupportedSubmissionFile(file)) {
      setMsg({ type: 'error', text: 'Desteklenmeyen dosya türü. PDF, JPG, PNG veya Word belgesi yükleyebilirsiniz.' });
      return;
    }
    if (file.size > MAX_SUBMISSION_SIZE) {
      setMsg({ type: 'error', text: 'Dosya boyutu 20 MB sınırını aşıyor.' });
      return;
    }
    setSelectedFile(file);
    setMsg(null);
  }

  async function handleSubmit() {
    if (!selectedFile) {
      setMsg({ type: 'error', text: 'Lütfen ödev dosyanızı yükleyin.' });
      return;
    }
    setSubmitting(true);
    setMsg(null);
    try {
      const safeName = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '-').toLowerCase();
      const path = `submissions/${userId}/${assignment.id}/${Date.now()}-${safeName}`;

      // Teslimi güncellerken eski dosyayı kaldır.
      if (existing?.file_path) {
        await supabase.storage.from('zehrahoca-submissions').remove([existing.file_path]);
      }

      const { error: uploadError } = await supabase.storage
        .from('zehrahoca-submissions')
        .upload(path, selectedFile);
      if (uploadError) throw new Error(`Dosya yüklenemedi: ${uploadError.message}`);

      const payload = {
        assignment_id: assignment.id,
        student_id: userId,
        status: 'teslim_edildi',
        note: note.trim() || null,
        link: link.trim() || null,
        file_path: path,
        file_name: selectedFile.name,
        file_type: selectedFile.type,
        file_size: selectedFile.size,
        submitted_at: new Date().toISOString(),
      };

      if (existing) {
        const { error: updateError } = await supabase
          .from('zehrahoca_assignment_submissions')
          .update(payload)
          .eq('id', existing.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('zehrahoca_assignment_submissions')
          .insert(payload);
        if (insertError) throw insertError;
      }

      setMsg({ type: 'success', text: 'Ödeviniz başarıyla teslim edildi.' });
      onSubmitted();
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setMsg({ type: 'error', text: (err as Error).message || 'Teslim edilirken bir hata oluştu.' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/40 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white">
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <div>
            <h3 className="font-heading text-base font-bold text-foreground-950">Ödev Teslim Et</h3>
            <p className="mt-0.5 text-xs text-foreground-500">{assignment.title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-foreground-500 transition hover:bg-background-100 cursor-pointer"
            aria-label="Kapat"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <p className="text-sm text-foreground-600">
            Çözdüğünüz ödevi PDF, fotoğraf veya belge olarak yükleyebilirsiniz.
          </p>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">Ödev Dosyanız</label>
            <div
              className={`relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition ${
                dragOver ? 'border-primary-400 bg-primary-50' : 'border-background-300 bg-background-50'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFile(e.dataTransfer.files?.[0] ?? null);
              }}
            >
              <i className="ri-upload-cloud-2-line text-3xl text-primary-500" />
              {selectedFile ? (
                <div className="px-2">
                  <p className="break-all text-sm font-medium text-foreground-800">{selectedFile.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{formatFileSize(selectedFile.size)}</p>
                </div>
              ) : (
                <p className="text-sm text-foreground-600">Dosya seçin veya buraya sürükleyin</p>
              )}
              <p className="text-xs text-foreground-400">PDF, JPG, PNG, DOC, DOCX · en fazla 20 MB</p>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>

          <div>
            <label htmlFor="teslim-not" className="mb-1.5 block text-sm font-medium text-foreground-700">
              Açıklama / Not <span className="text-foreground-400">(opsiyonel)</span>
            </label>
            <textarea
              id="teslim-not"
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              placeholder="Ödevinizle ilgili kısa bir açıklama yazabilirsiniz..."
            />
            <p className="mt-1 text-right text-xs text-foreground-400">{note.length}/500</p>
          </div>

          <div>
            <label htmlFor="teslim-link" className="mb-1.5 block text-sm font-medium text-foreground-700">
              Ek Kaynak Linki <span className="text-foreground-400">(opsiyonel)</span>
            </label>
            <input
              id="teslim-link"
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              placeholder="https://..."
            />
          </div>

          {msg && (
            <div
              className={`rounded-lg px-4 py-2.5 text-sm ${
                msg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {msg.text}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-background-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-lg border border-background-300 bg-white px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
          >
            Vazgeç
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
            {submitting ? 'Gönderiliyor...' : 'Ödevi Teslim Et'}
          </button>
        </div>
      </div>
    </div>
  );
}